import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { RDVButton, TitleSection, Inner } from "../../components/commonWidgets";
import { FiCalendar } from "react-icons/fi";
import styled from "styled-components";
import HighlightTitle from "../../components/HighlightTitle";
import apiClient from "../../services/apiClient";

const options = [
  "Site vitrine",
  "E-commerce",
  "Application web",
  "Application mobile",
  "Application de bureau",
  "Autres",
];

const Container = styled(Inner)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  max-width: 800px;
  text-align: center;
  padding-top: 2rem;
`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    offer: "",
    pages: "",
    contentCreation: false,
    deadline: "",
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
    file: null,
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (type === "file") {
      setFormData({ ...formData, file: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.offer) newErrors.offer = "Obligatoire";
    if (!formData.name) newErrors.name = "Obligatoire";
    if (!formData.email) newErrors.email = "Obligatoire";
    if (!formData.message) newErrors.message = "Obligatoire";
    if (formData.file) {
      const allowedTypes = [
        "text/plain",
        "image/png",
        "image/jpeg",
        "image/tiff",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(formData.file.type)) {
        newErrors.file = "Type de fichier non autorisé";
      }
      if (formData.file.size > 3 * 1024 * 1024) {
        newErrors.file = "Fichier trop volumineux (max 3 Mo)";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");

    if (!validate()) return;

    try {
      await apiClient.request("/api/contact", {
        method: "POST",
        body: JSON.stringify({
          offer: formData.offer,
          pages: formData.pages,
          contentCreation: formData.contentCreation,
          deadline: formData.deadline,
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          recaptchaToken,
        }),
      });
      setStatus("Message envoyé avec succès.");
    } catch (err) {
      console.error(err);
      setStatus("Erreur d'envoi du formulaire.");
    }
    setRecaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ maxWidth: 800, mx: "auto", marginBottom: "2rem", p: 0 }}
      >
        <HighlightTitle
          text="Contactez-nous"
          highlightWord="Contactez"
          bg="var(--my-color3)"
          rotateText={false}
          mainTextColor="var(--my-color3)"
          textColor="white"
        />
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<FiCalendar />}
            sx={{
              margin: "1rem auto 0rem auto",
              color: "var(--my-color5)",
              fontWeight: 500,
              textTransform: "uppercase",
              "&:hover": { background: "var(--my-color4)" },
            }}
            onClick={() => {
              if (window.Calendly) {
                window.Calendly.initPopupWidget({
                  url: "https://calendly.com/vdeliencourt-digibuddy/30min",
                });
              } else {
                window.open(
                  "https://calendly.com/vdeliencourt-digibuddy/30min",
                  "_blank"
                );
              }
            }}
          >
            Ou Réserver un créneau
          </Button>
        </Box>
        <Container>
          <TextField
            select
            fullWidth
            name="offer"
            label="Sélectionner une offre *"
            value={formData.offer}
            onChange={handleChange}
            error={!!errors.offer}
            helperText={errors.offer}
            sx={{ mb: 2 }}
          >
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="number"
            fullWidth
            name="pages"
            label="Nombre de pages"
            value={formData.pages}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.contentCreation}
                onChange={handleChange}
                name="contentCreation"
              />
            }
            label="Création de contenus (logos, visuels, etc.)"
          />

          <TextField
            type="number"
            fullWidth
            name="deadline"
            label="Délais souhaité (mois)"
            value={formData.deadline}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            name="name"
            label="Nom Prénom *"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            name="company"
            label="Entreprise"
            value={formData.company}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            name="phone"
            label="Téléphone"
            value={formData.phone}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            name="email"
            label="Email *"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            name="message"
            label="Message *"
            value={formData.message}
            onChange={handleChange}
            error={!!errors.message}
            helperText={errors.message}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" gutterBottom>
            Fichier autorisé : txt, png, jpg, jpeg, tiff, pdf, doc, docx. Max 3
            Mo.
          </Typography>
          <input type="file" name="file" onChange={handleChange} />
          {errors.file && <Typography color="error">{errors.file}</Typography>}

          <ReCAPTCHA
            style={{ marginTop: "1rem" }}
            ref={recaptchaRef}
            sitekey="6LdbgDMrAAAAAMFZgiPxEzSevx5W2suOmIFgxPqF"
            onChange={(token) => {
              // token est automatiquement envoyé quand l'utilisateur coche
              console.log(token);
              setRecaptchaToken(token);
            }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{
              margin: "auto",
              background: "var(--my-color3)",
              color: "white",
              padding: "8px 20px",
              borderRadius: "8px",
              marginTop: "1rem",
              width: "100%",
              textTransform: "uppercase",
              fontWeight: 400,
              "&:hover": {
                background: "var(--my-color4)",
              },
            }}
          >
            Envoyer
          </Button>

          {status && (
            <Typography
              sx={{ mt: 2 }}
              color={status.includes("succès") ? "success.main" : "error.main"}
            >
              {status}
            </Typography>
          )}
        </Container>
      </Box>
    </>
  );
};

export default ContactSection;
